import React, { useEffect, useRef, useState } from "react";
import { X } from "lucide-react";
import { useAppSelector } from "@redux/hooks";
import { useViews } from "./ViewsProvider";
import { MAX_VIEWS, viewLensLabel, viewTitle, type View } from "./viewsModel";

/**
 * The open views (project 13D), inside the header's title row.
 *
 * They share that row instead of adding one: the header already spends two rows on the
 * title and the route's sub-tabs, and a third would cost every screen its height (intent
 * §8, unknown 2). The active view *is* the page's heading — SideNav drops its own title
 * while one is active — and the rest sit beside it. With no views this renders nothing,
 * so the header measures exactly what it did before the feature (intent I6).
 *
 * Mouse: click to go to a view, middle click or the X to close it, drag to reorder.
 * Keyboard: arrows move between views, Enter goes, Delete closes, Alt+Shift+arrows
 * reorder — the drag has a keyboard equivalent, or reordering would be mouse-only.
 */
export default function ViewsBar() {
  const { views, activeId, activateView, closeView, moveView, flash, notice, dismissNotice } =
    useViews();
  const tenantType = useAppSelector((state) => state.userProfile.TenantType);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const chipRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const [dragId, setDragId] = useState<string | null>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  // A drag ends with a pointerup that the browser follows with a click; that click must
  // not also activate the view that was just dropped.
  const suppressClickRef = useRef(false);

  // On a narrow screen the bar scrolls: keep the view the user is in, and the one that
  // just glowed, where they can see it.
  const revealId = flash?.id ?? activeId;
  useEffect(() => {
    if (revealId) tabRefs.current[revealId]?.scrollIntoView?.({ block: "nearest", inline: "nearest" });
  }, [revealId, flash?.nonce]);

  if (views.length === 0) return null;

  // The lens only tells views apart when they differ. When every view shares it, the rail's
  // selector already says it once; repeating it on each view was noise (it stays in the
  // tooltip either way).
  const lensLabels = views.map((view) => viewLensLabel(view.selection, tenantType));
  const lensesDiffer = new Set(lensLabels).size > 1;

  const focusTab = (index: number) => {
    const view = views[(index + views.length) % views.length];
    if (view) tabRefs.current[view.id]?.focus();
  };

  const onTabKeyDown = (event: React.KeyboardEvent, view: View, index: number) => {
    const reorder = event.altKey && event.shiftKey;
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      const step = event.key === "ArrowRight" ? 1 : -1;
      if (reorder) {
        moveView(view.id, index + step);
        // Focus follows the view to its new place once the bar re-renders.
        window.requestAnimationFrame(() => tabRefs.current[view.id]?.focus());
      } else {
        focusTab(index + step);
      }
    } else if (event.key === "Home") {
      event.preventDefault();
      focusTab(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusTab(views.length - 1);
    } else if (event.key === "Delete" || event.key === "Backspace") {
      event.preventDefault();
      closeView(view.id);
      window.requestAnimationFrame(() => focusTab(Math.min(index, views.length - 2)));
    }
  };

  /** The slot the pointer is over: before the first view whose middle is to its right. */
  const slotAt = (clientX: number) => {
    for (let i = 0; i < views.length; i++) {
      const rect = chipRefs.current[views[i].id]?.getBoundingClientRect();
      if (rect && clientX < rect.left + rect.width / 2) return i;
    }
    return views.length;
  };

  /**
   * Reorder by dragging, on pointer events rather than HTML5 drag and drop: native DnD
   * does not start reliably from the buttons a view is made of, and paints a ghost image
   * of the whole chip. A few pixels of travel turn a press into a drag, so a click stays a
   * click.
   */
  const onChipPointerDown = (event: React.PointerEvent, view: View) => {
    if (event.button !== 0 || (event.target as Element).closest("[data-view-close]")) return;
    const startX = event.clientX;
    let dragging = false;

    const onMove = (move: PointerEvent) => {
      if (!dragging && Math.abs(move.clientX - startX) < 5) return;
      dragging = true;
      setDragId(view.id);
      setDropIndex(slotAt(move.clientX));
    };
    const onUp = (up: PointerEvent) => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      setDragId(null);
      setDropIndex(null);
      if (!dragging || up.type === "pointercancel") return;
      const from = views.findIndex((v) => v.id === view.id);
      const slot = slotAt(up.clientX);
      // Dropping past one's own slot shifts every index after it by one.
      moveView(view.id, from < slot ? slot - 1 : slot);
      suppressClickRef.current = true;
      window.setTimeout(() => {
        suppressClickRef.current = false;
      }, 0);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
  };

  return (
    <nav
      aria-label="Open views"
      data-testid="views-bar"
      // Zero basis: in the header's wrapping title row, a content-sized basis would push the
      // views onto a line of their own — the very height this layout exists to save.
      className="relative flex min-w-0 flex-[1_1_0%] items-center gap-2"
    >
      <div
        role="tablist"
        aria-label="Open views"
        className={`flex min-w-0 flex-1 items-center gap-0.5 overflow-x-auto ${dragId ? "cursor-grabbing select-none" : ""}`}
      >
        {views.map((view, index) => {
          const isActive = view.id === activeId;
          const title = viewTitle(view);
          const lens = lensLabels[index];
          const shownLens = lensesDiffer ? lens : null;
          const isFlashing = flash?.id === view.id;
          const fullName = lens ? `${title} — ${lens}` : title;

          return (
            <div
              key={view.id}
              data-testid="view-tab"
              data-active={isActive || undefined}
              ref={(element) => {
                chipRefs.current[view.id] = element;
              }}
              onPointerDown={(event) => onChipPointerDown(event, view)}
              className={`group relative flex h-7 min-w-[64px] max-w-[240px] shrink items-center rounded-md transition-colors ${
                isActive
                  ? "bg-semantic-state-active-brand text-semantic-text-brand"
                  : "text-semantic-text-secondary hover:bg-semantic-state-active hover:text-semantic-text-primary"
              } ${dragId === view.id ? "opacity-50" : ""}`}
            >
              {dropIndex === index && dragId !== view.id && (
                <span aria-hidden className="absolute -left-1 top-1 bottom-1 w-0.5 rounded bg-semantic-border-brand-primary" />
              )}
              {dropIndex === index + 1 && index === views.length - 1 && dragId !== view.id && (
                <span aria-hidden className="absolute -right-1 top-1 bottom-1 w-0.5 rounded bg-semantic-border-brand-primary" />
              )}
              {isFlashing && (
                <span
                  key={flash?.nonce}
                  aria-hidden
                  data-testid="view-flash"
                  className="view-flash pointer-events-none absolute inset-0 rounded-md"
                />
              )}
              <button
                ref={(element) => {
                  tabRefs.current[view.id] = element;
                }}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive || (!activeId && index === 0) ? 0 : -1}
                title={fullName}
                onClick={() => {
                  if (suppressClickRef.current) return;
                  activateView(view.id);
                }}
                onAuxClick={(event) => {
                  if (event.button !== 1) return;
                  event.preventDefault();
                  closeView(view.id);
                }}
                onMouseDown={(event) => {
                  if (event.button === 1) event.preventDefault();
                }}
                onKeyDown={(event) => onTabKeyDown(event, view, index)}
                className="relative flex h-full min-w-0 flex-1 items-center gap-1.5 rounded-md pl-2.5 pr-0.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-semantic-border-brand-primary"
              >
                {/* Every view in one size and weight (CTX-1399): the active one reads by its
                    background and colour, and a size of its own made the row look uneven and
                    shifted its neighbours whenever another view became active. Views only
                    open detail pages, which carry their own heading. */}
                <span className="min-w-0 truncate text-sm font-medium">
                  {title}
                </span>
                {shownLens && (
                  <span className="min-w-0 shrink-[2] truncate text-xs font-normal opacity-70">{shownLens}</span>
                )}
              </button>
              <button
                type="button"
                aria-label={`Close view ${title}`}
                data-view-close
                title="Close view"
                tabIndex={-1}
                onClick={() => closeView(view.id)}
                // Its room is always kept, so showing it on hover moves nothing; on an
                // inactive view it is only invisible — and not clickable — until hovered
                // or focused (CTX-1416).
                className={`relative mr-1 flex h-5 w-5 shrink-0 items-center justify-center rounded text-current hover:bg-semantic-state-active focus:outline-none ${
                  isActive
                    ? "opacity-60 hover:opacity-100"
                    : "pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-60 group-focus-within:pointer-events-auto group-focus-within:opacity-60"
                }`}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          );
        })}
      </div>

      <span
        className={`shrink-0 text-xs tabular-nums ${
          views.length >= MAX_VIEWS ? "text-semantic-text-warning" : "text-semantic-text-tertiary"
        }`}
        aria-label={`${views.length} of ${MAX_VIEWS} views open`}
      >
        {views.length}/{MAX_VIEWS}
      </span>

      {/* Floats over the page instead of taking room from the views it is about. */}
      {notice && (
        <button
          type="button"
          role="status"
          onClick={dismissNotice}
          className="absolute right-0 top-full z-30 mt-3 rounded-md border border-semantic-border-warning-secondary bg-semantic-bg-warning-secondary px-3 py-1.5 text-xs text-semantic-text-warning shadow-md"
        >
          {notice}
        </button>
      )}
    </nav>
  );
}
